import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown, faChevronLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';

const StateWiseCountsTable = ({ 
  apiResponse, 
  isLoading = false, 
  error = null,
  onStateSelect,
  showPercentage = true,
  showTotal = true,
  fetchDistrictData,
  // NEW PROPS FOR FILTERING
  selectedFinancialYear,
  selectedScheme,
  financialYearData = {}
}) => {
  // State for sorting
  const [sortConfig, setSortConfig] = React.useState({
    key: 'count',
    direction: 'desc'
  });

  // State for district view
  const [districtView, setDistrictView] = React.useState({
    isActive: false,
    stateName: '',
    stateCode: '',
    districtData: [],
    loading: false,
    error: null
  });

  // Filter data based on selected financial year AND scheme
  const filteredData = React.useMemo(() => {
    if (!apiResponse || !Array.isArray(apiResponse)) {
      return [];
    }

    let filtered = apiResponse;

    // Filter by scheme if selected
    if (selectedScheme && selectedScheme.schemeName) {
      filtered = filtered.filter(item => 
        item.schemeName === selectedScheme.schemeName
      );
    }

    // Filter by financial year if selected
    if (selectedFinancialYear) {
      filtered = filtered.filter(item => {
        // Check if item has financialYear property that matches
        if (item.financialYear) {
          return item.financialYear === selectedFinancialYear;
        }
        
        // Alternative: check if item exists in financialYearData mapping
        if (financialYearData[selectedFinancialYear]) {
          // This depends on your API structure - adjust accordingly
          return financialYearData[selectedFinancialYear].some(fyItem => 
            fyItem.stateCode === item.stateCode && 
            fyItem.schemeName === item.schemeName
          );
        }
        
        // If no specific financial year filtering logic, return true
        return true;
      });
    }

    return filtered;
  }, [apiResponse, selectedFinancialYear, selectedScheme, financialYearData]);

  // Process and sort the data
  const { schemeName, stateData, total } = React.useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return { schemeName: '', stateData: [], total: 0 };
    }

    // Group by state and sum counts (in case there are multiple entries for same state)
    const stateMap = new Map();
    
    filteredData.forEach(item => {
      const stateKey = item.stateName || 'Unknown State';
      const currentCount = parseInt(item.totalSurvey) || 0;
      
      if (stateMap.has(stateKey)) {
        stateMap.set(stateKey, stateMap.get(stateKey) + currentCount);
      } else {
        stateMap.set(stateKey, currentCount);
      }
    });

    // Transform to array format
    const transformedData = Array.from(stateMap.entries()).map(([stateName, count]) => {
      // Find the first matching item to get state codes
      const firstItem = filteredData.find(item => item.stateName === stateName);
      return {
        stateName: stateName,
        stateCode: firstItem?.stateCode || '',
        stateLGDCode: firstItem?.stateLGDCode || '',
        count: count,
        financialYear: firstItem?.financialYear,
        rawData: firstItem
      };
    });

    // Calculate total
    const calculatedTotal = transformedData.reduce((sum, item) => sum + item.count, 0);

    return {
      schemeName: selectedScheme?.schemeName || filteredData[0]?.schemeName || 'Selected Scheme',
      stateData: transformedData,
      total: calculatedTotal
    };
  }, [filteredData, selectedScheme]);

  // Apply sorting
  const sortedData = React.useMemo(() => {
    const sortableData = [...stateData];
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [stateData, sortConfig]);

  // Handle sort request
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon for header
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FontAwesomeIcon icon={faSort} />;
    return sortConfig.direction === 'asc' 
      ? <FontAwesomeIcon icon={faSortUp} /> 
      : <FontAwesomeIcon icon={faSortDown} />;
  };

  // Handle state click to fetch and show district data
  const handleStateClick = async (state) => {
    // Call the onStateSelect callback if provided
    if (onStateSelect) {
      onStateSelect(state);
    }

    // Check if we have the fetchDistrictData function and stateLGDCode
    if (fetchDistrictData && state.stateLGDCode) {
      setDistrictView({
        isActive: true,
        stateName: state.stateName,
        stateCode: state.stateCode,
        districtData: [],
        loading: true,
        error: null
      });

      try {
        // Fetch district data with flag=1, stateLGDCode, financial year and scheme
        const districtData = await fetchDistrictData(
          state.stateLGDCode, 
          1,
          selectedFinancialYear, // Pass financial year
          selectedScheme?.schemeName // Pass scheme name
        );
        
        setDistrictView({
          isActive: true,
          stateName: state.stateName,
          stateCode: state.stateCode,
          districtData: districtData || [],
          loading: false,
          error: null
        });
      } catch (err) {
        setDistrictView({
          isActive: true,
          stateName: state.stateName,
          stateCode: state.stateCode,
          districtData: [],
          loading: false,
          error: err.message || 'Failed to fetch district data'
        });
      }
    } else {
      // Fallback if fetchDistrictData is not provided
      setDistrictView({
        isActive: true,
        stateName: state.stateName,
        stateCode: state.stateCode,
        districtData: [],
        loading: false,
        error: 'District data fetching not configured'
      });
    }
  };

  // Handle back button click to return to state view
  const handleBackClick = () => {
    setDistrictView({
      isActive: false,
      stateName: '',
      stateCode: '',
      districtData: [],
      loading: false,
      error: null
    });
  };

  // Handle error state with retry option
  if (error) {
    return (
      <div className="alert alert-warning">
        <p>{error.message}</p>
        <button 
          className="btn btn-sm btn-outline-secondary"
          onClick={() => {
            // Retry without token
            if (typeof apiResponse === 'function') apiResponse();
          }}
        >
          Try without authentication
        </button>
      </div>
    );
  }

  // Handle loading state
  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 mb-0">Loading state data...</p>
        </div>
      </div>
    );
  }

  // Handle empty data
  if (stateData.length === 0) {
    return (
      <div className="card">
        <div className="card-body text-center text-muted">
          {selectedFinancialYear && selectedScheme
            ? `No data available for ${selectedScheme.schemeName} in financial year ${selectedFinancialYear}`
            : selectedFinancialYear
            ? `No data available for selected financial year: ${selectedFinancialYear}`
            : selectedScheme
            ? `No data available for scheme: ${selectedScheme.schemeName}`
            : 'No state-wise data available'
          }
        </div>
      </div>
    );
  }

  // District view
  if (districtView.isActive) {
    return (
      <div className="card mb-3 shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center">
          <button 
            className="btn btn-sm btn-light"
            onClick={handleBackClick}
          >
            <FontAwesomeIcon icon={faChevronLeft} className="me-1" />
            Back to States
          </button>
          <div className="text-center">
            <h5 className="mb-0">
              District-wise Counts for {districtView.stateName} ({districtView.stateCode})
            </h5>
            <div className="text-muted small">
              {selectedScheme?.schemeName} 
              {selectedFinancialYear && ` • FY: ${selectedFinancialYear}`}
            </div>
          </div>
          <span className="badge bg-light text-primary fs-6">
            Flag: 1
          </span>
        </div>
        <div className="card-body p-0">
          {districtView.loading ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading district data...</span>
              </div>
              <p className="mt-2 mb-0">Loading district data...</p>
            </div>
          ) : districtView.error ? (
            <div className="alert alert-warning m-3">
              <p>{districtView.error}</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-hover mb-0">
                <thead>
                  <tr>
                    <th>District Name</th>
                    <th className="text-end">Count</th>
                    <th className="text-end">Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {districtView.districtData.map((district, index) => (
                    <tr key={`${district.districtName}-${index}`}>
                      <td>{district.districtName}</td>
                      <td className="text-end">{district.count || 0}</td>
                      <td className="text-end">{district.flag}</td>
                    </tr>
                  ))}
                  {districtView.districtData.length === 0 && (
                    <tr>
                      <td colSpan="3" className="text-center text-muted">
                        No district data available for this state
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // State view (original)
  return (
    <div className="card mb-3 shadow-sm">
      <div className="card-header d-flex justify-content-between align-items-center">
        <div>
          <h5 className="mb-0">
            State-wise Counts for {schemeName}
          </h5>
          <div className="text-muted small">
            {selectedFinancialYear && `Financial Year: ${selectedFinancialYear}`}
            {!selectedFinancialYear && 'All Financial Years'}
          </div>
        </div>
        {total > 0 && (
          <span className="badge bg-light text-primary fs-6">
            Total: {total.toLocaleString()}
          </span>
        )}
      </div>
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-striped table-hover mb-0">
            <thead>
              <tr>
                <th 
                  className="cursor-pointer"
                  onClick={() => requestSort('stateName')}
                >
                  State {getSortIcon('stateName')}
                </th>
                <th 
                  className="text-end cursor-pointer"
                  onClick={() => requestSort('count')}
                >
                  Count {getSortIcon('count')}
                </th>
                {showPercentage && (
                  <th className="text-end">Percentage</th>
                )}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((state, index) => {
                const percentage = total > 0 
                  ? ((state.count / total) * 100).toFixed(1)
                  : 0;
                
                const hasStateLGDCode = !!state.stateLGDCode;

                return (
                  <tr 
                    key={`${state.stateName}-${index}`}
                    className={hasStateLGDCode ? 'cursor-pointer hover-row' : ''}
                  >
                    <td 
                      onClick={() => hasStateLGDCode && handleStateClick(state)}
                      className={hasStateLGDCode ? 'text-primary fw-bold clickable-state' : ''}
                      style={hasStateLGDCode ? { 
                        color: '#0d6efd', 
                        textDecoration: 'underline',
                        cursor: 'pointer'
                      } : {}}
                    >
                      {state.stateName}
                      {hasStateLGDCode && (
                        <FontAwesomeIcon 
                          icon={faArrowRight} 
                          className="ms-2" 
                          style={{ fontSize: '0.8em' }}
                        />
                      )}
                    </td>
                    <td className="text-end">{state.count.toLocaleString()}</td>
                    {showPercentage && (
                      <td className="text-end">
                        {percentage}%
                        <div className="progress mt-1" style={{ height: '4px' }}>
                          <div 
                            className="progress-bar bg-info" 
                            role="progressbar" 
                            style={{ width: `${percentage}%` }}
                            aria-valuenow={percentage}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              {showTotal && (
                <tr className="fw-bold bg-light">
                  <td>Total</td>
                  <td className="text-end">{total.toLocaleString()}</td>
                  {showPercentage && (
                    <td className="text-end">100%</td>
                  )}
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="card-footer text-muted small">
          {selectedFinancialYear 
            ? `Showing data for ${schemeName} in financial year ${selectedFinancialYear}`
            : `Showing data for ${schemeName} across all financial years`
          }
          {'. Click on state names with arrow icons to view district-wise data.'}
        </div>
      </div>
    </div>
  );
};

StateWiseCountsTable.propTypes = {
  apiResponse: PropTypes.arrayOf(
    PropTypes.shape({
      schemeName: PropTypes.string,
      stateName: PropTypes.string.isRequired,
      stateCode: PropTypes.string,
      stateLGDCode: PropTypes.string,
      totalSurvey: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
      ]).isRequired,
      financialYear: PropTypes.string
    })
  ),
  isLoading: PropTypes.bool,
  error: PropTypes.shape({
    message: PropTypes.string
  }),
  onStateSelect: PropTypes.func,
  showPercentage: PropTypes.bool,
  showTotal: PropTypes.bool,
  fetchDistrictData: PropTypes.func,
  // UPDATED PROPTYPES
  selectedFinancialYear: PropTypes.string,
  selectedScheme: PropTypes.shape({
    schemeName: PropTypes.string,
    schemeCode: PropTypes.string
  }),
  financialYearData: PropTypes.object
};

StateWiseCountsTable.defaultProps = {
  apiResponse: [],
  isLoading: false,
  error: null,
  showPercentage: true,
  showTotal: true,
  fetchDistrictData: null,
  selectedFinancialYear: null,
  selectedScheme: null,
  financialYearData: {}
};

export default StateWiseCountsTable;